/*
 * scroll-rail.js
 * Progressive enhancement for the shared mobile scroll rail.
 *
 * Wires the prev/next arrows rendered by snippets/scroll-rail-arrows.liquid to
 * the rail track. Markup contract (see exocet-2026.css "MOBILE SCROLL RAIL"):
 *   <div class="scroll-rail" data-rail-group>
 *     <button data-rail-prev> ... </button>
 *     <button data-rail-next> ... </button>
 *     <div class="...__grid" data-rail-track> ...cards... </div>
 *   </div>
 *
 * Without this script the rail still works as a native finger-swipe (the
 * arrows stay hidden because CSS only shows them under .scroll-rail--ready).
 */
(function () {
  if (window.__exocetScrollRailInit) return;
  window.__exocetScrollRailInit = true;

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function initRail(group) {
    if (!group || group.classList.contains("scroll-rail--ready")) return;
    // Infinite GSAP slider owns arrow wiring on desktop (product-grid-infinite.js).
    if (group.hasAttribute("data-infinite-rail") && window.matchMedia("(min-width: 750px)").matches) return;

    var events = new AbortController();
    var timers = new Set();
    function listen(target, type, handler, options) {
      if (target) target.addEventListener(type, handler, Object.assign({}, options, { signal: events.signal }));
    }
    group._destroyRail = function () {
      events.abort();
      timers.forEach(clearTimeout);
      group.classList.remove("scroll-rail--ready");
      delete group._destroyRail;
    };
    var track = group.querySelector("[data-rail-track]");
    if (!track) return;

    var prev = group.querySelector("[data-rail-prev]");
    var next = group.querySelector("[data-rail-next]");

    function stepDistance() {
      var first = track.children[0];
      if (!first) return track.clientWidth * 0.8;
      var w = first.getBoundingClientRect().width;
      // A `display: contents` direct child (used when a section flattens nested
      // grids into one rail) has no box of its own, so measure its first
      // laid-out descendant instead. Normal rails never hit this branch.
      if (w < 1) {
        var nodes = first.querySelectorAll("*");
        for (var i = 0; i < nodes.length; i++) {
          var dw = nodes[i].getBoundingClientRect().width;
          if (dw > 1) {
            w = dw;
            break;
          }
        }
      }
      if (w < 1) w = track.clientWidth * 0.8;
      var styles = getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return w + gap;
    }

    function scrollByDir(dir) {
      // Absolute clamped target rather than a relative scrollBy: keeps the rail
      // from overscrolling and lets update() detect the start/end edges cleanly.
      var maxLeft = track.scrollWidth - track.clientWidth;
      var target = Math.max(0, Math.min(maxLeft, track.scrollLeft + dir * stepDistance()));
      try {
        track.scrollTo({ left: target, behavior: reduceMotion.matches ? "auto" : "smooth" });
      } catch (e) {
        // Older engines that don't accept the options object.
        track.scrollLeft = target;
      }
      // Refresh disabled states once the scroll settles (the scroll listener
      // already covers smooth scrolling, this covers the non-animated path).
      var timer = window.setTimeout(function () { timers.delete(timer); update(); }, 200);
      timers.add(timer);
    }

    function update() {
      var canScroll = track.scrollWidth - track.clientWidth > 1;
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
      if (prev) prev.disabled = !canScroll || atStart;
      if (next) next.disabled = !canScroll || atEnd;
    }

    if (prev)
      listen(prev, "click", function (event) {
        event.preventDefault();
        scrollByDir(-1);
      });
    if (next)
      listen(next, "click", function (event) {
        event.preventDefault();
        scrollByDir(1);
      });
    listen(track, "scroll", update, { passive: true });
    listen(window, "resize", update);

    // Lazy-loaded card images mean the rail is usually measured before it has
    // its real width: scrollWidth ~= clientWidth, so update() disables both
    // arrows (CSS then hides them). Nothing re-measures on image load, so the
    // arrows would stay dead. Re-run update() as the layout settles.
    var imgs = track.querySelectorAll("img");
    for (var n = 0; n < imgs.length; n++) {
      if (imgs[n].complete) continue;
      listen(imgs[n], "load", update);
      listen(imgs[n], "error", update);
    }
    listen(window, "load", update);

    group.classList.add("scroll-rail--ready");
    update();
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var groups = scope.querySelectorAll("[data-rail-group]");
    for (var i = 0; i < groups.length; i++) initRail(groups[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Re-init sections re-rendered in the theme editor
  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
  document.addEventListener("shopify:section:unload", function (event) {
    event.target.querySelectorAll("[data-rail-group]").forEach(function (group) {
      if (group._destroyRail) group._destroyRail();
    });
  });
})();
